import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreIcon from "@mui/icons-material/MoreHorizOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import TagIcon from "@mui/icons-material/LocalOfferOutlined";
import ListIcon from "@mui/icons-material/FormatListNumberedOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/CloseOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageContainer, PageHeader } from "../components/PageHeader";
import AssetEditorDialog from "../components/distribution/AssetEditorDialog";
import VodWizardDialog from "../components/distribution/VodWizardDialog";
import TitleReadinessChip from "../components/distribution/TitleReadinessChip";
import VodDownloadDialog from "../components/distribution/VodDownloadDialog";
import { describeError, useToast } from "../components/toast";
import { saveErrorText } from "../components/distribution/findings";
import { foyerApi, type VodSeriesTree, type VodTitleRow } from "../http/foyer";
import {
  MIN_SEARCH_LENGTH,
  formatMatchReasons,
  type AppliedSort,
} from "@shared/asset-search";
import { draftToCreate, draftToUpdate } from "@shared/asset-draft";
import type { Genre, Provider } from "@shared/types/content-api";
import { VOD_ASSET_TYPES, type VodAssetType } from "@shared/vod-title";
import { downloadRefusalText, downloadSource } from "@shared/vod-download";
import { resolveVodThumbnail } from "@shared/vod-images";
import { tokens } from "../theme";
import { useFormat, type Formatters } from "../i18n/format";
import { PermissionGate } from "../auth/access";
import {
  AUTHOR_TITLES,
  useGate,
} from "../components/distribution/catalogueGates";

/**
 * VOD titles - the on-demand shelf, read from the Nenda Content API (`BE-15`).
 *
 * ## Constrained by design, not by optimisation
 *
 * Every list here sends a `type` filter AND a `sourceType`. That is not tuning:
 * `nenda-epg-ingest` mints one asset per EPG *airing*, forever, so an
 * unconstrained `GET /assets` is overwhelmingly yesterday's television with the
 * VOD catalogue buried in it. `live` and `catchup` are refused as query values
 * for the same reason - neither is authored here.
 *
 * ## What replaced the Published checkbox
 *
 * Nothing was lost, because there was nothing there. `CMSAsset.isPublished` in
 * the legacy portal is a hardcoded `return true` and its PUBLISHED column is
 * always ticked; that was the entire publishing UI. The column now shows the
 * one mechanism with real enforcement - the licence window - and the editor is
 * where you change it.
 *
 * ## The NENDA- reference is minted on request, never on a read
 *
 * It is the one thing in Distribution with no upstream equivalent, and it is
 * genuinely ours. A title imported by the portal carries none until somebody
 * needs to quote one, because heal-on-read over a catalogue dominated by
 * machine-generated catch-up rows would number hundreds of thousands of rows
 * nobody will ever cite and burn the sequence doing it.
 */

const PAGE_SIZE = 25;
const EM_DASH = "—";

/** The row's picture: whatever the resolver picked, or a generated tile. */
/**
 * The actions column stays pinned to the right edge, so Delete and Download are
 * reachable without scrolling even when the window is narrow enough that the
 * table has to scroll sideways. Opaque, or the columns scrolling underneath
 * would show through it.
 */
const stickyActionsSx = {
  position: "sticky",
  right: 0,
  bgcolor: "background.paper",
  zIndex: 1,
} as const;

function RowThumbnail({ row }: { row: VodTitleRow }) {
  // Resolved on every render from `images[]`, never stored. A `thumbnailUrl`
  // field would drift from the artwork within a month and then the list and the
  // editor would show different pictures with no way to tell which is wrong.
  const src = resolveVodThumbnail(
    {
      images: row.images ?? [],
      // No per-user language setting yet, so the operator language is first.
      // When there is one, this is the single line that reads it.
      language: "sv",
      languages: ["sv", "en"],
    },
    { width: 128, height: 72 }
  );
  if (!src) return <PosterTile title={row.originalTitle} id={row.assetId} />;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        width: 64,
        height: 36,
        borderRadius: 1,
        objectFit: "cover",
        border: `1px solid ${tokens.divider}`,
        display: "block",
      }}
    />
  );
}

/**
 * A stand-in poster.
 *
 * Deliberately NOT a remote image: a title with no artwork has nothing to point
 * an `<img>` at, and a URL that 404s reads as a bug rather than as a
 * placeholder. Null is a real answer from the resolver, and this is what the
 * caller draws for it.
 */
function PosterTile({ title, id }: { title: string; id: string }) {
  const hue = ([...id].reduce((n, c) => n + c.charCodeAt(0), 0) * 37) % 360;
  const initials = (title || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
  return (
    <Box
      aria-hidden
      sx={{
        width: 64,
        height: 36,
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        typography: "meta",
        fontWeight: "fontWeightBold",
        letterSpacing: 0.5,
        background: `linear-gradient(135deg, hsl(${hue} 52% 52%), hsl(${
          (hue + 40) % 360
        } 48% 38%))`,
      }}
    >
      {initials}
    </Box>
  );
}

function SortCaption({ sort }: { sort: AppliedSort | null }) {
  const { t } = useTranslation("content");
  if (!sort) return null;
  const order =
    sort.by === "relevance"
      ? t("vodTitlesPage.sort.bestMatch")
      : sort.by === "created" && sort.order === "desc"
        ? t("vodTitlesPage.sort.newest")
        : t("vodTitlesPage.sort.other", { by: sort.by, order: sort.order });
  return (
    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
      {sort.applied === "complete-set" && sort.scanned !== undefined
        ? t("vodTitlesPage.sort.completeSet", { order, count: sort.scanned })
        : t("vodTitlesPage.sort.catalogueOrder", { order })}
    </Typography>
  );
}

/**
 * Download, straight from the row. Disabled - with the reason on hover - when
 * the row itself already proves there is nothing downloadable (a series, an
 * encrypted-only or DASH-only title), using the same `downloadSource` the
 * server executes, so the button never offers what the server would refuse.
 * The manifest-dependent answers come from the dialog's plan.
 */
function RowDownloadButton({
  row,
  onOpen,
}: {
  row: VodTitleRow;
  onOpen: (row: VodTitleRow) => void;
}) {
  const { t } = useTranslation("content");
  const chosen = downloadSource(row);
  const refused =
    "refusal" in chosen ? downloadRefusalText(chosen.refusal) : null;
  return (
    <Tooltip title={refused ?? t("vodTitlesPage.download.tooltip")}>
      {/* A disabled button fires no pointer events; the span keeps the
          tooltip - which is the reason - reachable. */}
      <span>
        <IconButton
          size="small"
          aria-label={t("vodTitlesPage.download.aria", {
            name: row.originalTitle,
          })}
          data-testid={`vod-download-${row.assetId}`}
          disabled={refused !== null}
          onClick={() => onOpen(row)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

function shortDate(fmt: Formatters, iso: string | undefined): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  return Number.isNaN(ms)
    ? "—"
    : fmt.date(ms, { year: "numeric", month: "2-digit", day: "2-digit" });
}

/**
 * A series, as the server assembled it from sibling rows.
 *
 * Read-only on purpose: an episode is edited as the row it is. What this answers
 * is the question the shipped model got wrong by storing two integers - how many
 * seasons and episodes are really there - and it carries `complete`, so a page
 * count can never be presented as a total.
 */
function SeriesDialog({
  tree,
  loading,
  onClose,
}: {
  tree: VodSeriesTree | null;
  loading: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["content", "common"]);
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        {tree
          ? tree.series.originalTitle
          : t("vodTitlesPage.series.dialogTitle")}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {tree && (
          <Stack spacing={2}>
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("vodTitlesPage.series.counts", {
                seasons: tree.counts.seasons,
                episodes: tree.counts.episodes,
              })}
              {tree.counts.complete ? "" : t("vodTitlesPage.series.incomplete")}
            </Typography>
            {tree.seasons.map((season) => (
              <Box key={season.seasonNumber ?? "unnumbered"}>
                <Typography
                  variant="bodySm"
                  sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
                >
                  {t("vodTitlesPage.series.season", {
                    number: season.seasonNumber ?? "—",
                  })}
                  {season.season
                    ? ""
                    : t("vodTitlesPage.series.inferredSeason")}
                </Typography>
                <Stack sx={{ pl: 2, pt: 0.5 }}>
                  {season.episodes.map((episode) => (
                    <Typography
                      variant="meta"
                      key={episode.assetId}
                      sx={{ color: tokens.inkMuted }}
                    >
                      {t("vodTitlesPage.series.episodeLine", {
                        number: episode.episodeNumber ?? "—",
                        title: episode.originalTitle,
                      })}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ))}
            {tree.looseEpisodes.length > 0 && (
              <Alert severity="warning">
                {t("vodTitlesPage.series.loose", {
                  count: tree.looseEpisodes.length,
                })}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function VodTitlesPage() {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  const mayAuthor = useGate(AUTHOR_TITLES);
  const [rows, setRows] = useState<VodTitleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sort, setSort] = useState<AppliedSort | null>(null);
  const [counts, setCounts] = useState<Record<VodAssetType, number> | null>(
    null
  );

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [types, setTypes] = useState<VodAssetType[]>([]);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);

  const [dialog, setDialog] = useState<{ editing: VodTitleRow | null } | null>(
    null
  );
  const [wizard, setWizard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    code?: string;
    message: string;
  } | null>(null);

  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    title: VodTitleRow;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<VodTitleRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<VodTitleRow | null>(null);
  const [series, setSeries] = useState<{
    tree: VodSeriesTree | null;
    loading: boolean;
  } | null>(null);

  /**
   * `?open=<assetId>` opens that title's editor - the Overview page's
   * content-health list links here. Same contract as the channel list: read by
   * id, never searched for in the page in hand, and dropped once acted on.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const [openError, setOpenError] = useState<string | null>(null);
  useEffect(() => {
    if (!openId) return;
    let alive = true;
    foyerApi
      .getVodTitle(openId)
      .then((row) => {
        if (!alive) return;
        setSaveError(null);
        setOpenError(null);
        setDialog({ editing: row });
      })
      .catch((e) => {
        if (!alive) return;
        const err = e as { code?: string; message: string };
        setOpenError(saveErrorText(t, err.code, err.message));
      })
      .finally(() => {
        if (!alive) return;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("open");
            return next;
          },
          { replace: true }
        );
      });
    return () => {
      alive = false;
    };
  }, [openId, setSearchParams, t]);

  useEffect(() => {
    const trimmed = query.trim();
    const next = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : "";
    const timer = setTimeout(() => setSearch(next), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const typeKey = types.join(",");

  const load = useCallback(
    async (nextPage: number, mode: "replace" | "append") => {
      if (mode === "replace") setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const answer = await foyerApi.listVodTitles({
          page: nextPage,
          pageSize: PAGE_SIZE,
          ...(typeKey ? { type: typeKey.split(",") as VodAssetType[] } : {}),
          ...(search
            ? { search, sort: "relevance" as const, order: "desc" as const }
            : {}),
        });
        setRows((prev) =>
          mode === "append" ? [...prev, ...answer.titles] : answer.titles
        );
        setTotal(answer.pagination.total);
        setHasMore(answer.pagination.next !== null);
        setPage(nextPage);
        setSort(answer.sort);
      } catch (e) {
        const err = e as { code?: string; message: string };
        setError(saveErrorText(t, err.code, err.message));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search, typeKey, t]
  );

  useEffect(() => {
    void load(1, "replace");
  }, [load]);

  const refreshCounts = useCallback(() => {
    // Four `pagination.total` reads, one per type. Deliberately not derived
    // from the list page in hand: counting a page is the exact lie this whole
    // model was corrected to stop making.
    foyerApi
      .vodTitleCounts()
      .then(setCounts)
      .catch(() => setCounts(null));
  }, []);

  useEffect(() => {
    refreshCounts();
    foyerApi
      .listContentProviders()
      .then(setProviders)
      .catch(() => {});
    foyerApi
      .listContentGenres()
      .then(setGenres)
      .catch(() => {});
  }, [refreshCounts]);

  async function submitTitle(
    ...[draft, options]: Parameters<
      React.ComponentProps<typeof AssetEditorDialog>["onSubmit"]
    >
  ): Promise<boolean> {
    const editing = dialog?.editing ?? null;
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        await foyerApi.updateVodTitle(
          editing.assetId,
          draftToUpdate(draft, editing, options)
        );
      } else {
        // No `NENDA-` number is invented here. The server mints one on create,
        // against its own sequence, and answers with it - a client-side
        // reference would be a number nothing had reserved.
        await foyerApi.createVodTitle(draftToCreate(draft));
      }
      await load(1, "replace");
      refreshCounts();
      toast.success(
        editing
          ? t("vodTitlesPage.toasts.updated", {
              name: draft.originalTitle || editing.originalTitle,
            })
          : t("vodTitlesPage.toasts.created", { name: draft.originalTitle })
      );
      // The editor slides out, then its onClose clears `dialog`.
      return true;
    } catch (e) {
      const err = e as { code?: string; message: string };
      setSaveError({ code: err.code, message: err.message });
      const name = draft.originalTitle || editing?.originalTitle;
      toast.error(
        name
          ? describeError(t, "saveVodTitle", e, { name })
          : describeError(t, "saveVodTitleUnnamed", e)
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function removeTitle(title: VodTitleRow) {
    setDeleteError(null);
    try {
      await foyerApi.deleteVodTitle(title.assetId);
      setConfirmDelete(null);
      toast.success(
        t("vodTitlesPage.toasts.deleted", { name: title.originalTitle })
      );
      await load(1, "replace");
      refreshCounts();
    } catch (e) {
      const err = e as { code?: string; message: string };
      setDeleteError(saveErrorText(t, err.code, err.message));
      toast.error(
        describeError(t, "deleteVodTitle", e, { name: title.originalTitle })
      );
    }
  }

  async function mintReference(title: VodTitleRow) {
    try {
      const { reference } = await foyerApi.mintVodReference(title.assetId);
      setRows((prev) =>
        prev.map((r) => (r.assetId === title.assetId ? { ...r, reference } : r))
      );
      toast.success(
        t("vodTitlesPage.toasts.referenceAssigned", {
          reference,
          name: title.originalTitle,
        })
      );
    } catch (e) {
      toast.error(
        describeError(t, "assignVodReference", e, {
          name: title.originalTitle,
        })
      );
    }
  }

  async function openSeries(title: VodTitleRow) {
    setSeries({ tree: null, loading: true });
    try {
      setSeries({
        tree: await foyerApi.getVodSeries(title.assetId),
        loading: false,
      });
    } catch {
      setSeries(null);
    }
  }

  function toggleType(type: VodAssetType) {
    setTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("vodTitlesPage.eyebrow")}
        title={t("vodTitlesPage.title")}
        subtitle={t("vodTitlesPage.subtitle")}
        actions={
          <PermissionGate {...AUTHOR_TITLES}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              /**
               * Creating goes through the WIZARD; editing stays on the big form.
               *
               * Not a cosmetic split. Creation is where the order matters —
               * subtitles cannot be added after delivery, and the old flow let a
               * file picker commit a shared serial transcoder before anybody had
               * described the title. Editing an existing row has no such
               * ordering, and forcing it through eight steps would be worse.
               */
              onClick={() => {
                setSaveError(null);
                setWizard(true);
              }}
              data-testid="vod-create"
            >
              {t("vodTitlesPage.create")}
            </Button>
          </PermissionGate>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack spacing={0.75}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            {/* Each chip's number is a `pagination.total`, so it is the count of
                what EXISTS rather than of what came back. Selecting narrows the
                list; selecting none means all four. */}
            {VOD_ASSET_TYPES.map((type) => (
              <Chip
                key={type}
                size="small"
                data-testid={`vod-type-${type}`}
                label={
                  counts
                    ? t("vodTitlesPage.typeWithCount", {
                        label: t(`vodTitlesPage.types.${type}`),
                        count: counts[type],
                      })
                    : t(`vodTitlesPage.types.${type}`)
                }
                color={types.includes(type) ? "primary" : "default"}
                variant={types.includes(type) ? "filled" : "outlined"}
                onClick={() => toggleType(type)}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <Typography
              variant="meta"
              sx={{ color: tokens.inkMuted }}
              data-testid="vod-count"
            >
              {loading
                ? t("vodTitlesPage.loadingTitles")
                : t("vodTitlesPage.countOf", { shown: rows.length, total })}
            </Typography>
            <SortCaption sort={sort} />
          </Stack>
        </Stack>

        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("vodTitlesPage.searchPlaceholder")}
          data-testid="vod-search"
          helperText={
            query.trim().length > 0 && query.trim().length < MIN_SEARCH_LENGTH
              ? t("vodTitlesPage.minChars", { count: MIN_SEARCH_LENGTH })
              : " "
          }
          sx={{ width: { xs: "100%", sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("vodTitlesPage.clearSearch")}
                  onClick={() => setQuery("")}
                  data-testid="vod-search-clear"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="vod-error"
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void load(1, "replace")}
            >
              {t("vodTitlesPage.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {openError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setOpenError(null)}
          data-testid="vod-open-error"
        >
          {t("vodTitlesPage.openError", { reason: openError })}
        </Alert>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          {/*
            FIXED layout, so the table is exactly as wide as the page and NAME
            takes whatever the other columns leave. With the browser's automatic
            layout the Name column grew to its longest unbreakable title (a
            filename with no spaces is one word), the dates wrapped onto two
            lines, and the table became wider than the screen: the actions menu
            sat off the right edge and had to be scrolled to. Below `minWidth`
            the container scrolls instead of crushing the columns.
          */}
          <Table
            size="small"
            data-testid="vod-titles-table"
            sx={{
              tableLayout: "fixed",
              minWidth: 880,
              "& th, & td": { px: 1.25 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 84 }}>
                  {t("vodTitlesPage.columns.thumbnail")}
                </TableCell>
                <TableCell>{t("vodTitlesPage.columns.name")}</TableCell>
                <TableCell sx={{ width: 120 }}>
                  {t("vodTitlesPage.columns.reference")}
                </TableCell>
                <TableCell sx={{ width: 76 }}>
                  {t("vodTitlesPage.columns.type")}
                </TableCell>
                <TableCell sx={{ width: 148 }}>
                  {t("vodTitlesPage.columns.availability")}
                </TableCell>
                <TableCell sx={{ width: 96 }}>
                  {t("vodTitlesPage.columns.created")}
                </TableCell>
                <TableCell sx={{ width: 96 }}>
                  {t("vodTitlesPage.columns.changed")}
                </TableCell>
                <TableCell
                  sx={{
                    width: 84,
                    ...stickyActionsSx,
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Stack alignItems="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => {
                  const caption = row.matches
                    ? formatMatchReasons(row.matches, ["originalTitle"])
                    : null;
                  return (
                    <TableRow
                      key={row.assetId}
                      hover
                      data-testid={`vod-row-${row.assetId}`}
                      sx={{ cursor: "pointer" }}
                      // A row IS its draft, so the row opens straight into the
                      // asset. The ⋯ menu keeps the actions that are NOT
                      // "look at this" - series tree, reference, delete.
                      tabIndex={0}
                      onClick={() => {
                        setSaveError(null);
                        setDialog({ editing: row });
                      }}
                      onKeyDown={(e) => {
                        // Guarded on the row itself: the actions button inside
                        // fires its own Enter, and that keydown bubbles here.
                        if (e.key !== "Enter" || e.target !== e.currentTarget)
                          return;
                        e.preventDefault();
                        setSaveError(null);
                        setDialog({ editing: row });
                      }}
                    >
                      <TableCell>
                        <RowThumbnail row={row} />
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden" }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={row.originalTitle || undefined}
                          sx={{ fontWeight: "fontWeightSemibold" }}
                        >
                          {row.originalTitle || t("vodTitlesPage.noName")}
                        </Typography>
                        {/* Says why a row is here when the reason is not a
                            column - otherwise a search surfaces a title whose
                            name does not contain the word and the list looks
                            broken. Re-derived from the row in hand, because the
                            catalogue returns rows and not reasons. */}
                        {caption && (
                          <Typography
                            variant="micro"
                            sx={{ color: tokens.inkMuted, mt: 0.25 }}
                            data-testid={`vod-match-${row.assetId}`}
                          >
                            {caption}
                          </Typography>
                        )}
                        <Typography
                          variant="micro"
                          noWrap
                          sx={{
                            fontFamily: "fontFamilyMono",
                            color: tokens.inkMuted,
                          }}
                        >
                          {row.assetId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="meta"
                          noWrap
                          sx={{
                            fontFamily: "fontFamilyMono",
                            color: row.reference ? tokens.ink : tokens.inkMuted,
                          }}
                        >
                          {row.reference ?? EM_DASH}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="meta">{row.type}</Typography>
                      </TableCell>
                      <TableCell>
                        <TitleReadinessChip
                          availability={row.availability}
                          blockers={row.blockers}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="meta"
                          noWrap
                          sx={{ color: tokens.inkMuted }}
                        >
                          {shortDate(fmt, row.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="meta"
                          noWrap
                          sx={{ color: tokens.inkMuted }}
                        >
                          {shortDate(fmt, row.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                        sx={stickyActionsSx}
                      >
                        <RowDownloadButton row={row} onOpen={setDownloading} />
                        <IconButton
                          size="small"
                          aria-label={t("vodTitlesPage.actionsFor", {
                            name: row.originalTitle,
                          })}
                          data-testid={`vod-menu-${row.assetId}`}
                          onClick={(e) =>
                            setRowMenu({ anchor: e.currentTarget, title: row })
                          }
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {!loading && rows.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography
                      variant="body2"
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: tokens.inkMuted,
                      }}
                    >
                      {search
                        ? t("vodTitlesPage.noTitleMatches", { query: search })
                        : t("vodTitlesPage.noTitles")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Divider />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 2 }}
          >
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("vodTitlesPage.countOf", { shown: rows.length, total })}
            </Typography>
            {hasMore && (
              <Button
                endIcon={
                  loadingMore ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
                disabled={loadingMore}
                onClick={() => void load(page + 1, "append")}
                data-testid="vod-show-more"
                sx={{
                  fontSize: 13,
                  fontWeight: "fontWeightBold",
                  color: tokens.inkMuted,
                }}
              >
                {t("vodTitlesPage.showMore")}
              </Button>
            )}
          </Stack>
        </TableContainer>
      </Card>

      <Menu
        open={Boolean(rowMenu)}
        anchorEl={rowMenu?.anchor ?? null}
        onClose={() => setRowMenu(null)}
      >
        {mayAuthor && (
          <MenuItem
            onClick={() => {
              if (rowMenu) {
                setSaveError(null);
                setDialog({ editing: rowMenu.title });
              }
              setRowMenu(null);
            }}
            data-testid="vod-edit"
          >
            <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
            {t("common:buttons.edit")}
          </MenuItem>
        )}
        {rowMenu?.title.type === "series" && (
          <MenuItem
            onClick={() => {
              if (rowMenu) void openSeries(rowMenu.title);
              setRowMenu(null);
            }}
          >
            <ListIcon fontSize="small" sx={{ mr: 1.5 }} />
            {t("vodTitlesPage.menu.seasons")}
          </MenuItem>
        )}
        {mayAuthor && rowMenu && !rowMenu.title.reference && (
          <MenuItem
            onClick={() => {
              if (rowMenu) void mintReference(rowMenu.title);
              setRowMenu(null);
            }}
            data-testid="vod-mint-reference"
          >
            <TagIcon fontSize="small" sx={{ mr: 1.5 }} />
            {t("vodTitlesPage.menu.mintReference")}
          </MenuItem>
        )}
        {mayAuthor && <Divider />}
        {mayAuthor && (
          <MenuItem
            onClick={() => {
              if (rowMenu) {
                setDeleteError(null);
                setConfirmDelete(rowMenu.title);
              }
              setRowMenu(null);
            }}
            sx={{ color: tokens.error }}
            data-testid="vod-delete"
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
            {t("common:buttons.delete")}
          </MenuItem>
        )}
      </Menu>

      <VodDownloadDialog
        title={downloading}
        onClose={() => setDownloading(null)}
      />

      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>{t("vodTitlesPage.deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("vodTitlesPage.deleteDialog.body", {
              name: confirmDelete?.originalTitle,
            })}
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="delete-error">
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid="vod-delete-confirm"
            onClick={() => confirmDelete && void removeTitle(confirmDelete)}
          >
            {t("common:buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {series && (
        <SeriesDialog
          tree={series.tree}
          loading={series.loading}
          onClose={() => setSeries(null)}
        />
      )}

      <VodWizardDialog
        open={wizard}
        onClose={() => setWizard(false)}
        onFinished={() => {
          void load(1, "replace");
          refreshCounts();
        }}
      />

      {/* Keyed so opening a different title REMOUNTS rather than copying new
          props into old state - see the dialog's own note. */}
      {dialog && (
        <AssetEditorDialog
          key={dialog.editing?.assetId ?? "new"}
          open
          kind="title"
          initial={dialog.editing}
          providers={providers}
          genres={genres}
          saving={saving}
          error={saveError}
          onClose={() => setDialog(null)}
          onSubmit={submitTitle}
        />
      )}
    </PageContainer>
  );
}
